// src/pages/IDStock.jsx
import React, { useState } from 'react'
import { updateStockField, updateCash, fetchStockbitPrices, addStock, deleteStock } from '../lib/supabase'
import { Edit2, Check, X, TrendingUp, TrendingDown, RefreshCw, KeyRound, Plus, Trash2 } from 'lucide-react'

const fmt  = n  => n != null ? new Intl.NumberFormat('id-ID').format(Math.round(n)) : '—'
const fmtM = n  => n != null ? `${(n/1_000_000).toFixed(1)} jt` : '—'
const pct  = n  => n != null ? `${(n*100).toFixed(1)}%` : '—'
const pct2 = n  => n != null ? `${(n*100).toFixed(2)}%` : '—'

function MosBadge({ mos }) {
  const cls = mos >= 0.4 ? 'mos-high' : mos >= 0.2 ? 'mos-mid' : 'mos-low'
  return <span className={`mono ${cls}`}>{pct(mos)}</span>
}

function DriftCell({ drift }) {
  const cls = Math.abs(drift) < 0.005 ? 'drift-neu' : drift > 0 ? 'drift-pos' : 'drift-neg'
  return <span className={cls}>{drift >= 0 ? '+' : ''}{pct2(drift)}</span>
}

function GainCell({ gain }) {
  return (
    <span className={gain >= 0 ? 'text-green' : 'text-red'}>
      {gain >= 0 ? <TrendingUp size={11}/> : <TrendingDown size={11}/>}
      {' '}{(gain * 100).toFixed(1)}%
    </span>
  )
}

function AllocBar({ my, rk }) {
  const maxW = Math.max(my, rk, 0.01)
  return (
    <div style={{ display:'flex', alignItems:'center', gap:6, justifyContent:'flex-end' }}>
      <div className="bar-track" style={{ width:80 }}>
        <div className="bar-fill-b" style={{ width:`${Math.min(my/maxW*100,100)}%` }}/>
        <div className="bar-target" style={{ left:`${Math.min(rk/maxW*100,100)}%` }}/>
      </div>
      <span className="mono text-muted" style={{fontSize:11, minWidth:36}}>{pct(my)}</span>
    </div>
  )
}

// Generic inline-editable number cell
function EditableNum({ code, field, value, displayFn, inputDefault, parseFn, onSave }) {
  const [editing, setEditing] = useState(false)
  const [val, setVal]         = useState('')
  const [saving, setSaving]   = useState(false)

  const open = () => { setVal(String(inputDefault ?? value)); setEditing(true) }
  const save = async () => {
    const n = parseFn(val)
    if (n === null) return
    setSaving(true)
    try { await updateStockField(code, field, n); onSave() } finally { setSaving(false); setEditing(false) }
  }

  if (editing) return (
    <div className="edit-cell">
      <input className="edit-input" value={val} onChange={e => setVal(e.target.value)}
        onKeyDown={e => { if (e.key==='Enter') save(); if (e.key==='Escape') setEditing(false) }}
        autoFocus/>
      <Check size={13} className="edit-icon text-green" onClick={save}/>
      <X     size={13} className="edit-icon text-red"   onClick={() => setEditing(false)}/>
    </div>
  )
  return (
    <div className="edit-cell">
      <span className="mono">{displayFn(value)}</span>
      <Edit2 size={12} className="edit-icon" onClick={open}/>
    </div>
  )
}

const parsePrice = v => { const n = parseFloat(v); return (isNaN(n) || n <= 0) ? null : n }
const parseLot   = v => { const n = parseInt(v);   return (isNaN(n) || n < 0)  ? null : n }
const parseAlloc = v => { const n = parseFloat(v); return (isNaN(n) || n < 0 || n > 100) ? null : +(n / 100).toFixed(4) }

const BLANK = { code:'', intrinsic_value:'', total_lot:'0', cost_price:'', rk_cost:'', rk_alloc:'' }
const TOKEN_KEY = 'stockbit_token'

export default function IDStock({ portfolioData, onRefresh, loading }) {
  const [editingCash, setEditingCash]   = useState(null)
  const [cashVal, setCashVal]           = useState('')
  const [savingCash, setSavingCash]     = useState(false)
  const [refreshing, setRefreshing]     = useState(false)
  const [refreshError, setRefreshError] = useState(null)
  const [token, setToken]               = useState(() => localStorage.getItem(TOKEN_KEY) || process.env.REACT_APP_STOCKBIT_TOKEN || '')
  const [showToken, setShowToken]       = useState(false)
  const [tokenDraft, setTokenDraft]     = useState('')
  const [showAdd, setShowAdd]           = useState(false)
  const [addForm, setAddForm]           = useState(BLANK)
  const [addError, setAddError]         = useState(null)
  const [adding, setAdding]             = useState(false)

  if (!portfolioData) return null
  const { stocks, totalMktVal, totalCostVal, rdpt, rdn, cashInvestable } = portfolioData
  const totalRkAlloc = stocks.reduce((a, s) => a + s.rk_alloc, 0)

  // ── handlers ──────────────────────────────────────────────────────────────

  const saveCash = async (label) => {
    const n = parseFloat(cashVal.replace(/[^0-9.]/g,''))
    if (isNaN(n)) return
    setSavingCash(true)
    try { await updateCash(label, n); onRefresh() }
    finally { setSavingCash(false); setEditingCash(null) }
  }

  const saveToken = () => {
    const t = tokenDraft.trim()
    localStorage.setItem(TOKEN_KEY, t)
    setToken(t); setShowToken(false); setTokenDraft('')
  }

  const handleRefreshPrices = async () => {
    if (!token) { setShowToken(true); return }
    setRefreshing(true); setRefreshError(null)
    try {
      const { failed } = await fetchStockbitPrices(stocks.map(s => s.code), token)
      if (failed.length > 0) setRefreshError(`Gagal: ${failed.join(', ')}`)
      await onRefresh()
    } catch (e) { setRefreshError(e.message) }
    finally { setRefreshing(false) }
  }

  const handleAdd = async () => {
    const { code, intrinsic_value, total_lot, cost_price, rk_cost, rk_alloc } = addForm
    if (!code || !intrinsic_value || !cost_price || !rk_alloc) {
      setAddError('Kode, IV, Harga Modal, dan Target RK wajib diisi'); return
    }
    setAdding(true); setAddError(null)
    try {
      await addStock({
        code:            code.trim().toUpperCase(),
        intrinsic_value: parseFloat(intrinsic_value),
        total_lot:       parseInt(total_lot) || 0,
        cost_price:      parseFloat(cost_price),
        rk_cost:         rk_cost ? parseFloat(rk_cost) : null,
        rk_alloc:        +(parseFloat(rk_alloc) / 100).toFixed(4),
      })
      setAddForm(BLANK); setShowAdd(false); onRefresh()
    } catch (e) { setAddError(e.message) }
    finally { setAdding(false) }
  }

  const handleDelete = async (code) => {
    if (!window.confirm(`Hapus ${code} dari portfolio?`)) return
    try { await deleteStock(code); onRefresh() }
    catch (e) { alert(`Gagal hapus: ${e.message}`) }
  }

  const field = (key) => ({ value: addForm[key], onChange: e => setAddForm(f => ({...f, [key]: e.target.value})) })

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* ── Holdings table ── */}
      <div className="section-card">
        <div className="section-head">
          <div>
            <div className="section-title">ID Stock — Holdings</div>
            <div className="section-sub">
              Klik ✏️ untuk edit nilai · Bar biru = bobot saya, garis merah = target RK
            </div>
          </div>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <button className="refresh-btn" onClick={handleRefreshPrices} disabled={refreshing||loading}
              title={token ? 'Fetch harga dari Stockbit' : 'Set token Stockbit dulu'}>
              <RefreshCw size={12} className={refreshing ? 'spin' : ''}/>
              {refreshing ? 'Mengambil...' : 'Refresh Harga'}
            </button>
            <button className="refresh-btn" style={{gap:5}}
              onClick={() => { setTokenDraft(token); setShowToken(v => !v) }}
              title="Set Stockbit Bearer token">
              <KeyRound size={12}/>
              <span style={{width:6,height:6,borderRadius:'50%',flexShrink:0,
                background: token ? 'var(--green)' : 'var(--red)'}}/>
            </button>
            <button className="refresh-btn"
              onClick={() => { setShowAdd(v => !v); setAddError(null) }}
              title="Tambah emiten baru">
              <Plus size={12}/>
              Tambah
            </button>
            <div style={{display:'flex',gap:12,fontSize:11,color:'var(--text3)'}}>
              <span>■ <span style={{color:'var(--blue)'}}>My Alloc</span></span>
              <span>| <span style={{color:'var(--red)'}}>RK Target</span></span>
            </div>
          </div>
        </div>

        {/* Token input */}
        {showToken && (
          <div style={{padding:'14px 20px',borderBottom:'1px solid var(--border)',
            background:'var(--bg3)',display:'flex',flexDirection:'column',gap:8}}>
            <div style={{fontSize:11,color:'var(--text3)'}}>
              Stockbit Bearer Token — buka DevTools → Network → salin dari header <code>Authorization</code>
            </div>
            <textarea
              style={{width:'100%',minHeight:72,padding:'8px 10px',background:'var(--bg4)',
                border:'1px solid var(--border2)',borderRadius:6,color:'var(--text)',
                fontFamily:'var(--mono)',fontSize:11,resize:'vertical',outline:'none'}}
              placeholder="Paste token di sini (tanpa kata 'Bearer')"
              value={tokenDraft} onChange={e => setTokenDraft(e.target.value)}/>
            <div style={{display:'flex',gap:8}}>
              <button className="btn btn-primary" style={{fontSize:12,padding:'5px 14px'}} onClick={saveToken}>Simpan Token</button>
              <button className="btn btn-ghost" style={{fontSize:12,padding:'5px 14px'}} onClick={() => setShowToken(false)}>Batal</button>
              {token && (
                <button className="btn btn-ghost" style={{fontSize:12,padding:'5px 14px',color:'var(--red)'}}
                  onClick={() => { localStorage.removeItem(TOKEN_KEY); setToken(''); setShowToken(false) }}>
                  Hapus Token
                </button>
              )}
            </div>
          </div>
        )}

        {/* Add form */}
        {showAdd && (
          <div style={{padding:'16px 20px',borderBottom:'1px solid var(--border)',background:'var(--bg3)'}}>
            <div style={{fontSize:11,color:'var(--text3)',marginBottom:12,fontWeight:500}}>
              Tambah Emiten Baru
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:10}}>
              {[
                { label:'Kode*',            key:'code',            placeholder:'BBCA' },
                { label:'Nilai Intrinsik*', key:'intrinsic_value', placeholder:'5000' },
                { label:'Jumlah Lot',       key:'total_lot',       placeholder:'0' },
                { label:'Harga Modal Saya*',key:'cost_price',      placeholder:'4800' },
                { label:'Harga Modal RK',   key:'rk_cost',         placeholder:'4200' },
                { label:'Target RK %*',     key:'rk_alloc',        placeholder:'10.5' },
              ].map(f => (
                <div key={f.key}>
                  <div style={{fontSize:10,color:'var(--text3)',marginBottom:4}}>{f.label}</div>
                  <input className="input" style={{fontSize:12}} placeholder={f.placeholder} {...field(f.key)}/>
                </div>
              ))}
            </div>
            {addError && <div style={{fontSize:11,color:'var(--red)',marginBottom:8}}>{addError}</div>}
            <div style={{display:'flex',gap:8}}>
              <button className="btn btn-primary" style={{fontSize:12,padding:'5px 14px'}}
                onClick={handleAdd} disabled={adding}>
                {adding ? 'Menyimpan...' : 'Simpan'}
              </button>
              <button className="btn btn-ghost" style={{fontSize:12,padding:'5px 14px'}}
                onClick={() => { setShowAdd(false); setAddForm(BLANK); setAddError(null) }}>
                Batal
              </button>
            </div>
          </div>
        )}

        {refreshError && (
          <div className="error-banner" style={{margin:'8px 16px',fontSize:11}}>{refreshError}</div>
        )}

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Kode</th>
                <th>MoS</th>
                <th>Nilai Intrinsik</th>
                <th>Lot</th>
                <th>Harga Modal</th>
                <th>Harga Pasar</th>
                <th>Gain/Loss</th>
                <th>Nilai Modal (Rp)</th>
                <th>Nilai Pasar (Rp)</th>
                <th>Bobot Saya</th>
                <th>Target RK</th>
                <th>Drift</th>
                <th>Harga Modal RK</th>
                <th>Deviasi vs RK</th>
                <th style={{width:32}}></th>
              </tr>
            </thead>
            <tbody>
              {stocks.map(s => (
                <tr key={s.code}>
                  <td><span className="code-chip">{s.code}</span></td>
                  <td><MosBadge mos={s.mos}/></td>
                  <td>
                    <EditableNum code={s.code} field="intrinsic_value" value={s.intrinsic_value}
                      displayFn={fmt} parseFn={parsePrice} onSave={onRefresh}/>
                  </td>
                  <td>
                    <EditableNum code={s.code} field="total_lot" value={s.total_lot}
                      displayFn={v => String(v)} parseFn={parseLot} onSave={onRefresh}/>
                  </td>
                  <td>
                    <EditableNum code={s.code} field="cost_price" value={s.cost_price}
                      displayFn={fmt} parseFn={parsePrice} onSave={onRefresh}/>
                  </td>
                  <td className="mono">{fmt(s.price)}</td>
                  <td><GainCell gain={s.gainPct}/></td>
                  <td className="mono">{fmtM(s.costVal)}</td>
                  <td className="mono">{fmtM(s.mktVal)}</td>
                  <td><AllocBar my={s.myAlloc} rk={s.rk_alloc}/></td>
                  <td>
                    <EditableNum code={s.code} field="rk_alloc" value={s.rk_alloc}
                      displayFn={pct} inputDefault={(s.rk_alloc * 100).toFixed(1)}
                      parseFn={parseAlloc} onSave={onRefresh}/>
                  </td>
                  <td><DriftCell drift={s.drift}/></td>
                  <td>
                    <EditableNum code={s.code} field="rk_cost" value={s.rk_cost}
                      displayFn={fmt} parseFn={parsePrice} onSave={onRefresh}/>
                  </td>
                  <td>
                    <span className={s.deviation >= 0 ? 'text-green' : 'text-red'}>
                      {s.deviation >= 0 ? '+' : ''}{pct(s.deviation)}
                    </span>
                  </td>
                  <td style={{padding:'0 8px'}}>
                    <Trash2 size={12} className="edit-icon"
                      style={{color:'var(--text3)'}}
                      onClick={() => handleDelete(s.code)}/>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid var(--border2)', background:'var(--bg3)' }}>
                <td colSpan={7} style={{textAlign:'left',padding:'10px 12px',color:'var(--text3)',fontSize:11}}>TOTAL</td>
                <td className="mono" style={{fontWeight:500}}>{fmtM(totalCostVal)}</td>
                <td className="mono" style={{fontWeight:500}}>{fmtM(totalMktVal)}</td>
                <td colSpan={2}></td>
                <td className="mono">{pct(totalRkAlloc)}</td>
                <td colSpan={3}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* ── Cash positions ── */}
      <div className="section-card">
        <div className="section-head">
          <div className="section-title">Posisi Cash</div>
        </div>
        <div style={{ padding:20, display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12 }}>
          {[
            { label:'RDPT Cash', val:rdpt, key:'RDPT Cash' },
            { label:'RDN Cash',  val:rdn,  key:'RDN Cash'  },
            { label:'Total Investable', val:cashInvestable, readOnly:true },
          ].map(c => (
            <div key={c.label} style={{background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:8,padding:'14px 16px'}}>
              <div style={{fontSize:10,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:4}}>{c.label}</div>
              {editingCash === c.key && !c.readOnly ? (
                <div style={{display:'flex',gap:6,alignItems:'center',marginTop:4}}>
                  <input className="input" style={{fontSize:13}} value={cashVal}
                    onChange={e => setCashVal(e.target.value)}
                    onKeyDown={e => { if(e.key==='Enter') saveCash(c.key); if(e.key==='Escape') setEditingCash(null) }}
                    autoFocus/>
                  <Check size={14} className="edit-icon text-green" onClick={() => saveCash(c.key)}/>
                  <X size={14} className="edit-icon text-red" onClick={() => setEditingCash(null)}/>
                </div>
              ) : (
                <div style={{display:'flex',alignItems:'baseline',gap:8,marginTop:2}}>
                  <span style={{fontSize:18,fontWeight:500,color:'var(--text)'}}>Rp {fmtM(c.val)}</span>
                  {!c.readOnly && (
                    <Edit2 size={12} className="edit-icon" style={{marginTop:2}}
                      onClick={() => { setCashVal(String(c.val ?? '')); setEditingCash(c.key) }}/>
                  )}
                </div>
              )}
              <div style={{fontSize:11,color:'var(--text3)',marginTop:4}}>Rp {fmt(c.val)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── MoS visual ── */}
      <div className="section-card">
        <div className="section-head">
          <div className="section-title">Margin of Safety Overview</div>
          <div style={{fontSize:11,color:'var(--text3)'}}>
            <span className="mos-high">■ ≥40%</span>{' · '}
            <span className="mos-mid">■ 20–40%</span>{' · '}
            <span className="mos-low">■ &lt;20%</span>
          </div>
        </div>
        <div style={{padding:16,display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
          {[...stocks].sort((a,b) => b.mos - a.mos).map(s => (
            <div key={s.code} style={{background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:6,padding:'10px 12px'}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:8}}>
                <span className="code-chip" style={{fontSize:11}}>{s.code}</span>
                <MosBadge mos={s.mos}/>
              </div>
              <div className="bar-track" style={{width:'100%',height:6,borderRadius:3}}>
                <div style={{height:6,borderRadius:3,
                  width:`${Math.min(s.mos*100/60*100,100)}%`,
                  background: s.mos>=0.4 ? 'var(--green)' : s.mos>=0.2 ? 'var(--amber)' : 'var(--red)'}}/>
              </div>
              <div style={{fontSize:10,color:'var(--text3)',marginTop:6}}>
                IV: {fmt(s.intrinsic_value)} · Pasar: {fmt(s.price)}
              </div>
              <div style={{fontSize:10,color:'var(--text3)'}}>
                Upside: <span className="text-blue">{pct(s.upside)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
